<template>
  <div
    v-if="sale"
    class="mt-auto bg-gray-100 -m-6 py-6 px-4 md:px-6 rounded-md print:bg-white print:mt-0 print:pt-0 print:h-screen print:overflow-visible"
  >
    <div class="bg-white p-4 rounded-md shadow overflow-x-auto print:shadow-none print:pt-0">
      <div class="block sm:flex justify-between print:flex">
        <div v-if="$settings.login_logo_url">
          <div class="my-2 w-80 max-h-[100px] flex items-start justify-start">
            <img :src="$settings.login_logo_url" :alt="$settings.name" class="max-h-full max-w-full rounded object-cover" />
          </div>
        </div>
        <tec-application-logo v-else class="mb-4 sm:mb-0" />
        <div class="text-left sm:text-right leading-snug max-w-md print:text-right">
          <div class="font-bold">{{ $company.company_name }}</div>
          <div v-if="$company?.gst" class="font-bold">GST: {{ $company.gst }}</div>
          <div v-if="$company?.vat" class="font-bold">VAT: {{ $company.vat }}</div>
          <div>
            <span class="mr-1" v-if="$company?.address">{{ $company.address }}</span>
            <span class="mr-1" v-if="$company?.city">{{ $company.city }}</span>
            <span class="mr-1" v-if="$company?.state">{{ $company.state }}</span>
            <span class="mr-1" v-if="$company?.postal_code">{{ $company.postal_code }}</span>
            <span v-if="$company?.country">{{ $company.country }}</span>
          </div>
          <div v-if="$company?.phone">{{ $company.phone }}</div>
          <div v-if="$company?.email">{{ $company.email }}</div>
        </div>
      </div>
      <div class="border-b my-4 -mx-4"></div>

      <trashed-message v-if="sale.draft == 1" class="mb-4 h-12 print:hidden" :action="false" icon="info">
        {{ $t('This record is still a draft.') }}
      </trashed-message>
      <trashed-message v-if="sale.deleted_at" class="mb-4 h-12 print:hidden" :action="false">
        {{ $t('This record has been deleted.') }}
      </trashed-message>

      <div class="py-4 w-full">
        <h1 class="text-xl text-center uppercase font-extrabold">{{ $t('Sale') }}</h1>
        <svg id="barcode" class="mt-2 mx-auto"></svg>
      </div>

      <div class="block sm:flex justify-between print:flex">
        <div class="w-full sm:w-1/2 leading-snug mb-6 sm:mb-0">
          <div class="text-sm font-bold">&nbsp;</div>
          <div>{{ $t('Number') }}: {{ sale.id }}</div>
          <div>{{ $t('Locator') }}: {{ sale.locator_number }}</div>
          <div>{{ $t('Date') }}: {{ $date(sale.date) }}</div>
          <div>{{ $t('Created at') }}: {{ $datetime(sale.created_at) }}</div>
        </div>
        <div class="text-left w-full sm:w-1/2 leading-snug">
          <div class="text-sm font-bold">{{ $t('Billed to') }}:</div>
          <div>{{ sale.customer.name }}</div>
          <div v-if="sale.customer?.phone">{{ $t('Tel') }}: {{ sale.customer.phone }}</div>
          <div v-if="sale.customer?.email">{{ $t('Email') }}: {{ sale.customer.email }}</div>
        </div>
      </div>

      <div class="-mx-4 overflow-x-auto">
        <table class="w-full mt-8 mb-4" style="min-width: 500px">
          <thead>
            <tr>
              <th scope="col" class="py-3.5 pl-4 pr-3 text-center text-sm font-semibold text-gray-900 sm:pl-6 w-12">No.</th>
              <th scope="col" class="text-left text-sm font-semibold text-gray-900">
                <div class="px-3 py-2 w-full min-w-[150px]">{{ $t('Description') }}</div>
              </th>
              <!-- <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-40">{{$t('Quantity')}}</th> -->
              <th scope="col" class="text-center text-sm font-semibold text-gray-900">
                <div class="px-3 py-2 w-24">{{ $t('Tax') }}</div>
              </th>
              <th scope="col" class="text-center text-sm font-semibold text-gray-900">
                <div class="px-3 py-2 w-24">{{ $t('HST') }}</div>
              </th>
              <template v-if="$can('show-details')">
                <th scope="col" :class="print_details ? 'p-0' : 'p-0 w-0'" class="text-center text-sm font-semibold text-gray-900">
                  <div :class="print_details ? 'px-3 py-2 w-24' : 'print:hidden px-3 py-3.5 w-24'">{{ $t('Agent Commission') }}</div>
                </th>
                <th scope="col" :class="print_details ? 'p-0' : 'p-0 w-0'" class="text-center text-sm font-semibold text-gray-900">
                  <div :class="print_details ? 'px-3 py-3.5 w-24' : 'print:hidden px-3 py-3.5 w-24'">{{ $t('Commission') }}</div>
                </th>
                <th scope="col" :class="print_details ? 'p-0' : 'p-0 w-0'" class="text-center text-sm font-semibold text-gray-900">
                  <div :class="print_details ? 'px-3 py-3.5 w-24' : 'print:hidden px-3 py-3.5 w-24'">{{ $t('HST on Comm.') }}</div>
                </th>
              </template>
              <th scope="col" class="px-3 py-2 text-center text-sm font-semibold text-gray-900 w-40">{{ $t('Airfare') }}</th>
              <!-- <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-40">{{$t('Total')}}</th> -->
            </tr>
          </thead>

          <template v-for="(item, ii) in sale.items" :key="'i_' + item.id">
            <tbody class="group avoid" :class="{ 'bg-gray-50': ii % 2 != 0, 'border-b': sale.items.length == ii + 1 }">
              <tr>
                <td class="group-hover:bg-gray-100 border-t px-6 py-2 font-bold">{{ ii + 1 }}</td>
                <td class="group-hover:bg-gray-100 border-t px-6 py-2 font-bold">{{ $t(item.description) }}</td>
                <!-- <td class="group-hover:bg-gray-100 border-t px-6 py-2 text-right">{{ $number(item.quantity) }}</td> -->
                <td class="group-hover:bg-gray-100 border-t px-6 py-2 text-right">{{ $number(item.tax) }}</td>
                <td class="group-hover:bg-gray-100 border-t px-6 py-2 text-right">{{ $number(item.hst) }}</td>
                <template v-if="$can('show-details')">
                  <td class="group-hover:bg-gray-100 border-t text-right" :class="print_details ? 'px-6 py-2' : 'p-0 w-0'">
                    <span :class="{ 'print:hidden': !print_details }">{{ $number(item.agent_commission) }}</span>
                  </td>
                  <td class="group-hover:bg-gray-100 border-t text-right" :class="print_details ? 'px-6 py-2' : 'p-0 w-0'">
                    <span :class="{ 'print:hidden': !print_details }">{{ $number(item.commission) }}</span>
                  </td>
                  <td class="group-hover:bg-gray-100 border-t text-right" :class="print_details ? 'px-6 py-2' : 'p-0 w-0'">
                    <span :class="{ 'print:hidden': !print_details }">{{ $number(item.hst_on_commission) }}</span>
                  </td>
                </template>
                <!-- <template v-if="$can('show-details')"> -->
                <td class="group-hover:bg-gray-100 border-t px-6 py-2 text-right">
                  {{ $number(Number(item.airfare) + Number(item.commission)) }}
                </td>
                <!-- </template>
                <template v-else>
                  <td class="group-hover:bg-gray-100 border-t px-6 py-2 text-right">
                    {{ $number(item.total) }}
                  </td>
                </template> -->
                <!-- <td class="group-hover:bg-gray-100 border-t px-6 py-2 text-right">{{ $number(item.total) }}</td> -->
              </tr>
            </tbody>
          </template>

          <tfoot>
            <tr>
              <td class="group-hover:bg-gray-100 border-t px-6 py-2 font-bold text-right" :colspan="$can('show-details') ? 7 : 4">
                {{ $t('Airfare') }}
              </td>
              <td class="group-hover:bg-gray-100 border-t px-6 py-2 font-bold text-right">{{ $number(sale.total) }}</td>
            </tr>
            <tr>
              <td class="group-hover:bg-gray-100 border-t px-6 py-2 font-bold text-right" :colspan="$can('show-details') ? 7 : 4">
                {{ $t('Total Tax') }}
              </td>
              <td class="group-hover:bg-gray-100 border-t px-6 py-2 font-bold text-right">{{ $number(sale.total_tax) }}</td>
            </tr>
            <tr>
              <td class="group-hover:bg-gray-100 border-t px-6 py-2 font-bold text-right" :colspan="$can('show-details') ? 7 : 4">
                {{ $t('Grand Total') }}
              </td>
              <td class="group-hover:bg-gray-100 border-t px-6 py-2 font-bold text-right">{{ $number(sale.grand_total) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <dl class="flex flex-wrap gap-y-4">
        <div class="w-full sm:w-1/2 print:w-1/2">
          <dt class="text-sm font-medium text-gray-500">{{ $t('Status') }}</dt>
          <dd class="mt-1 text-sm text-gray-900"><Status :status="sale.status" /></dd>
        </div>
        <div class="w-full sm:w-1/2 print:w-1/2">
          <dt class="text-sm font-medium text-gray-500">{{ $t('Payment Method') }}</dt>
          <dd class="mt-1 text-sm text-gray-900">{{ $t(sale.payment_method) }}</dd>
        </div>
        <template v-if="sale.cc_fees">
          <div class="w-full sm:w-1/2 print:w-1/2">
            <dt class="text-sm font-medium text-gray-500">{{ $t('Credit Card Fee') }}</dt>
            <dd class="mt-1 text-sm text-gray-900">{{ $t(sale.cc_fees) }}</dd>
          </div>
          <div class="w-full sm:w-1/2 print:w-1/2">
            <dt class="text-sm font-medium text-gray-500"></dt>
            <dd class="mt-1 text-sm text-gray-900"></dd>
          </div>
        </template>
        <div class="w-full sm:w-1/2 print:w-1/2">
          <dt class="text-sm font-medium text-gray-500">{{ $t('Medical Insurance') }}</dt>
          <dd class="mt-1 text-sm text-gray-900">{{ sale.medical_insurance ? 'Yes' : 'No' }}</dd>
        </div>
        <div class="w-full sm:w-1/2 print:w-1/2">
          <dt class="text-sm font-medium text-gray-500">{{ $t('Travel Insurance') }}</dt>
          <dd class="mt-1 text-sm text-gray-900">{{ sale.travel_insurance ? 'Yes' : 'No' }}</dd>
        </div>
      </dl>

      <!-- <div v-if="sale.attachments && sale.attachments.length" class="print:hidden py-4 w-full">
        <dt class="font-medium">{{ $t('Attachments') }}</dt>
        <dd class="mt-1 text-sm text-gray-900">
          <ul role="list" class="border border-gray-200 rounded-md divide-y divide-gray-200">
            <li
              v-for="attachment in sale.attachments"
              :key="attachment.id"
              class="pl-3 pr-4 py-3 flex items-center justify-between text-sm"
            >
              <div class="w-0 flex-1 flex items-center">
                <icons name="clip" class="flex-shrink-0 h-5 w-5 text-gray-400" />
                <span class="ml-2 flex-1 w-0 truncate"> {{ attachment.title }} </span>
              </div>
              <div class="ml-4 flex-shrink-0">
                <a class="font-medium text-indigo-600 hover:text-indigo-500" :href="route('media.download', attachment.id)">
                  {{ $t('Download') }}
                </a>
              </div>
            </li>
          </ul>
        </dd>
      </div> -->

      <div v-if="sale.comment" class="mt-4 py-4 w-full">
        {{ $t(sale.comment) }}
      </div>
    </div>
    <div class="mt-auto pt-4 w-full text-center text-sm text-gray-500 hidden print:block">
      {{ $t('This is computer generated document, no signature required.') }}
    </div>
  </div>
</template>

<script>
import JsBarcode from 'jsbarcode';
import Status from './Status.vue';
import TrashedMessage from '@/Shared/TrashedMessage.vue';
import TecApplicationLogo from '@/Jetstream/ApplicationLogo.vue';

export default {
  props: { sale: Object },

  components: { Status, TrashedMessage, TecApplicationLogo },

  data() {
    return {
      loading: false,
      print_details: false,
    };
  },

  mounted() {
    // JsBarcode('.barcode').init();
    JsBarcode('#barcode', this.sale.id, {
      width: 1,
      margin: 0,
      fontSize: 0,
      height: '20',
      format: 'CODE128',
    });
  },

  updated() {
    JsBarcode('#barcode', this.sale.id, {
      width: 1,
      margin: 0,
      fontSize: 0,
      height: '20',
      format: 'CODE128',
    });
  },

  methods: {},
};
</script>
